// NOTE: This is eventually meant to be generated via GitHub actions.
import React from "react";
import { Link } from "gatsby";
import Img from "gatsby-image";
import {
  Code,
  IL,
  Warning,
  CodeRef,
  Response,
  Remark,
} from "~components/documentation";

import { ArrowRightOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import Latex from "react-latex";
import color from "~styles/color";

/**
 * Variable reference.
 */
function VRef() {}

let objectId = (name: string = '"Apple|1|1|1"') => {
  return {
    name: "objectId",
    type: "string",
    example: name,
    required: true,
    description: (
      <p>
        The target object's <IL>objectId</IL>, found in the object's metadata.
      </p>
    ),
  };
};

let forceAction = {
  name: "forceAction",
  type: "bool",
  default: "False",
  description: (
    <p>
      If <IL>False</IL>, the agent will not be able to interact with the object
      unless it is within the initialized <IL>visibilityDistance</IL>of the
      object and the object appears in the agent's current frame. This prevents
      the agent from unnaturally interacting with objects that are too far away.
    </p>
  ),
};

/**
 * Adds a video to the documentation.
 *
 * @param props.aspectRatio the height / width of the video. Assumes 1 / 1 aspect ratio by default.
 * @param props.id the video id from Vimeo.
 */
export let Video = (props: {
  id: string;
  aspectRatio?: number;
  marginTop?: string;
  marginBottom?: string;
  autoplay?: 1 | 0;
}) => (
  <div
    css={css`
      text-align: center;
      position: relative;
      overflow: hidden;
      width: 100%;
      padding-top: calc(
        100% * ${props.aspectRatio === undefined ? 1 : props.aspectRatio}
      );
      background-color: ${color.gray5};
      margin: auto;
      margin-bottom: ${props.marginBottom === undefined
        ? "25px"
        : props.marginBottom};
      margin-top: ${props.marginTop};
    `}
  >
    <iframe
      src={`https://player.vimeo.com/video/${props.id}?autoplay=${
        props.autoplay !== undefined ? props.autoplay : 1
      }&loop=1&muted=1&autopause=0`}
      frameBorder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      css={css`
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 100%;
      `}
    />
  </div>
);

const Demo = () => (
  <Link to="/demo">
    <div
      css={css`
        padding-left: 15px;
        padding-right: 15px;
        padding-top: 10px;
        padding-bottom: 10px;
        background-color: ${color.light.blue6};
        border-radius: 3px;
        color: white;
        font-weight: 600;
        margin-bottom: 5px;
        transition-duration: 0.3s;
        &:hover {
          background-color: ${color.light.blue5};
        }
      `}
    >
      Test it live on the demo <ArrowRightOutlined />
    </div>
  </Link>
);

/**
 * Section Reference.
 */

// Agent class
interface Parameter {
  name: string;
  default?: string;
  type: string;
  description: React.ReactNode | string;
  required?: boolean;
  example?: string;
}

function replaceParams(example: string, parameters: any[]) {
  let index = example.indexOf("[params]");

  let code: any = [example.substring(0, index)];
  for (const param of parameters[0]) {
    code.push(
      <CodeRef
        code={`${param.name}=${
          param.example === undefined ? param.default : param.example
        }`}
        onHover={() => parameters[1](param.name)}
        onMouseOut={parameters[2]}
        paramName={param.name}
        methodKey={parameters[3]}
      />
    );
    code.push(",\n    ");
  }

  code[code.length - 1] = example.substring(index + "[params]".length);
  return code;
}

function ActionExample(props: {
  actionName: string;
  parameters: any[];
  includeEvent?: boolean;
  caption?: string;
}) {
  return (
    <Code caption={props.caption}>
      {replaceParams(
        String.raw`${
          props.includeEvent === true ? "event = " : ""
        }controller.step(
    action="${props.actionName}",
    [params]
)`,
        props.parameters
      )}
    </Code>
  );
}

export const UnityAPI = {
  BaseAgent: {
    SetMassProperties: {
      argsTitle: "Set Mass Properties Parameters",
      description: (
        <p>
          <IL>SetMassProperties</IL> changes the mass properties of any{" "}
          <IL>Pickupable</IL> or <IL>Moveable</IL> object.
        </p>
      ),
      example: (...parameters) => (
        <ActionExample actionName="SetMassProperties" parameters={parameters} />
      ),
      parameters: [
        {
          name: "objectId",
          type: "str",
          example: '"Apple|+1.25|+0.25|-0.75"',
          description: (
            <p>
              The unique identifier of a <IL>Moveable</IL> object, found in the
              object metadata.
            </p>
          ),
          required: true,
        },
        {
          name: "mass",
          required: true,
          example: 22.5,
          type: "float",
          description: (
            <p>
              <Latex>
                The new object's mass, in kilograms. Must be greater than $0$.
              </Latex>
            </p>
          ),
        },
        {
          name: "drag",
          required: true,
          example: 15.9,
          type: "float",
          description: (
            <p>
              <Latex>
                The new drag coefficient of the object, which determines its
                resistance when it is in motion. Higher values slow the object
                down more. Must be greater than $0$.
              </Latex>
            </p>
          ),
        },
        {
          name: "angularDrag",
          required: true,
          example: 5.6,
          type: "float",
          description: (
            <p>
              <Latex>
                The new angular drag coefficient of the object, which determines
                its resistance when it is in rotational motion. Higher values
                make it harder to rotate the object. Must be greater than $0$.
              </Latex>
            </p>
          ),
        },
      ],
    },

    initialization: {
      argsTitle: "Controller Parameters",
      description: (
        <p>
          After installing AI2-THOR, we can initialize a controller, which will
          allow us to execute actions in the environment.
        </p>
      ),
      example: (...parameters) => (
        <Code>
          {replaceParams(
            String.raw`from ai2thor.controller import Controller

controller = Controller(
    [params]
)`,
            parameters
          )}
        </Code>
      ),
      parameters: [
        {
          name: "agentMode",
          required: true,
          type: "str",
          example: '"locobot"',
          description: (
            <>
              <p>
                Always set this to <IL>locobot</IL> in order to use the LoCoBot
                agent, which was designed for RoboTHOR.
              </p>
              <Warning>
                If unset, the agent will be the default iTHOR agent, which is
                not the LoCoBot, and thus has different properties than these
                docs.
              </Warning>
              <p>
                A RuntimeError is raised if <IL>agentMode</IL> is not set to
                "default", "locobot", "drone", or "arm".
              </p>
            </>
          ),
        },
        {
          name: "visibilityDistance",
          description: (
            <>
              <p>
                Used to set the maximum goal distance in meters between the
                agent and an object. This affects each object's visible
                property, where when <IL>True</IL>, the agent is within{" "}
                <IL>visibilityDistance</IL> of that object and the object
                appears in the agent's current egocentric frame.
              </p>
              <Remark>
                Visibility is defined on{" "}
                <Link to="/ithor/documentation/concepts/#object-visibility">
                  iTHOR's concepts page
                </Link>
                .
              </Remark>
            </>
          ),
          type: "float",
          default: "1.5",
        },
        {
          name: "scene",
          description: (
            <p>
              The name of which scene to initialize. Valid RoboTHOR scenes are
              in the form <IL>{String.raw`FloorPlan_Train{1:12}_{1:5}`}</IL>
              or <IL>{String.raw`FloorPlan_Val{1:3}_{1:5}`}</IL>, such as{" "}
              <IL>FloorPlan_Train3_5</IL> or <IL>FloorPlan_Val1_2</IL>.
            </p>
          ),
          type: "Optional[str]",
          default: "None",
          example: '"FloorPlan_Train1_3"',
        },
        {
          name: "gridSize",
          type: "float",
          description: (
            <p>
              The mean amount, in meters, that the agent moves after calling a
              move action (i.e., <IL>MoveAhead</IL> and <IL>MoveBack</IL>). The
              full amount that the agent moves is sampled from{" "}
              <Latex>{String.raw`$$\mathcal N(\mu=\texttt{gridSize}, \sigma=\texttt{movementGaussianSigma})$$`}</Latex>
              .
            </p>
          ),
          default: "0.25",
        },
        {
          name: "movementGaussianSigma",
          type: "float",
          default: "0.005",
          description: (
            <p>
              The standard deviation, in meters, controlling the amount of noise
              in a move action (i.e., <IL>MoveAhead</IL> and <IL>MoveBack</IL>).
              The full amount that the agent moves is sampled from{" "}
              <Latex>{String.raw`$$\mathcal N(\mu=\texttt{gridSize}, \sigma=\texttt{movementGaussianSigma})$$`}</Latex>
              .
            </p>
          ),
        },
        {
          name: "rotateStepDegrees",
          type: "float",
          default: "90",
          description: (
            <p>
              The mean amount, in degrees, that the agent rotates after calling
              a rotate action (i.e., <IL>RotateLeft</IL> or <IL>RotateRight</IL>
              ). The full amount that the agent rotates is then sampled from{" "}
              <Latex>{String.raw`$$\mathcal N(\mu=\texttt{rotateStepDegrees}, \sigma=\texttt{rotateGaussianSigma})$$`}</Latex>
              .
            </p>
          ),
        },
        {
          name: "rotateGaussianSigma",
          type: "float",
          default: "0.5",
          description: (
            <p>
              The standard deviation, in degrees, controlling the amount of
              noise in a rotate action (i.e., <IL>RotateLeft</IL> or{" "}
              <IL>RotateRight</IL>
              ). The full amount that the agent rotates is then sampled from{" "}
              <Latex>{String.raw`$$\mathcal N(\mu=\texttt{rotateStepDegrees}, \sigma=\texttt{rotateGaussianSigma})$$`}</Latex>
              .
            </p>
          ),
        },
        {
          name: "renderDepthImage",
          type: "bool",
          default: "False",
          description: (
            <>
              <p>
                When <IL>True</IL>, a depth frame is rendered and made available
                as an <Link to="#event-depth_frame">event.depth_frame</Link>{" "}
                attribute.
              </p>
              <p>
                We require this to be explicitly passed in because rendering
                depth takes longer than rendering RGB alone.
              </p>
            </>
          ),
        },
        {
          name: "renderInstanceSegmentation",
          type: "bool",
          default: "False",
          description: (
            <>
              <p>
                When <IL>True</IL>, a instance segmentation frame is rendered
                and made available as an{" "}
                <IL>Event.instance_segmentation_frame</IL> attribute. Instance
                segmentation provides segmentation by object instance, where all
                objects in view are distinguishable.
              </p>
              <p>
                We require this to be explicitly passed in because rendering
                instance segmentation takes longer than rendering RGB alone.
              </p>
            </>
          ),
        },
        // {
        //   name: "renderSemanticSegmentation",
        //   type: "bool",
        //   default: "False",
        //   description: (
        //     <>
        //       <p>
        //         When <IL>True</IL>, a instance segmentation frame is rendered
        //         and made available as an{" "}
        //         <IL>Event.instance_segmentation_frame</IL> attribute. Instance
        //         segmentation provides segmentation by object instance, where all
        //         objects in view are distinguishable.
        //       </p>
        //       <p>
        //         We require this to be explicitly passed in because rendering
        //         instance segmentation takes longer than rendering RGB alone.
        //       </p>
        //     </>
        //   ),
        // },
        {
          name: "width",
          type: "int",
          default: "300",
          description: (
            <p>
              The number of horizontal sampled pixels for each frame. This
              affects every rendered image frame (e.g., RGB, depth, BGR).
            </p>
          ),
        },
        {
          name: "height",
          type: "int",
          default: "300",
          description: (
            <p>
              The number of vertical sampled pixels for each frame. This affects
              every rendered image frame (e.g., RGB, depth, BGR).
            </p>
          ),
        },
        {
          name: "fieldOfView",
          type: "int",
          default: "60",
          description: (
            <>
              <p>
                Changes the camera's optical field of view, in degrees. Valid
                values are in the domain <Latex>$$(0:180)$$</Latex>. The default
                field of view for the LoCoBot is 60 degrees.
              </p>
              <Remark>
                <p>
                  Not all agent modes have the same default field of view. For
                  instance, the drone's field of view is 150 degrees, by
                  default.
                </p>
              </Remark>
            </>
          ),
        },
      ],
    },

    RandomizeMaterials: {
      argsTitle: "Randomize Materials Parameters",
      description: (
        <p>
          Randomizes all the materials in the scene. Each object may only
          randomize to sensible materials from a similar material type. For
          instance, an <IL>Apple</IL>'s materials won't change to an{" "}
          <IL>Orange</IL>'s materials, and a <IL>Chair</IL>'s material won't
          change to a <IL>Plant</IL>'s material. Empirically, the rooms
          generated from material randomization look incredibly realistic in
          most cases. Upon calling reset, materials change back to their
          original values.
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="RandomizeMaterials"
            parameters={parameters}
          />
          <Video
            id="552684137"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="10px"
          />
          <Demo />
        </>
      ),
      parameters: [
        {
          name: "useTrainMaterials",
          default: "None",
          type: "Optional[bool]",
          description: (
            <>
              <p>
                Specifies if the randomization should include materials found in
                training rooms. Training rooms consist of all of the following:
              </p>
              <ul>
                <li>
                  <Latex>{String.raw`Kitchens: $\text{FloorPlan}[1:20]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`Living Rooms: $\text{FloorPlan}[201:220]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`Bedrooms: $\text{FloorPlan}[301:320]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`Bathrooms: $\text{FloorPlan}[401:420]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`RoboTHOR Train: $\text{FloorPlan}\_\text{Train}*\_*$`}</Latex>
                </li>
              </ul>
              <p>
                If the agent is in one of the above scenes and <IL>None</IL> is
                passed in, then <IL>useTrainMaterials</IL> will default to{" "}
                <IL>True</IL>. Otherwise, it will default to <IL>False</IL>.
              </p>
              <p>
                An exception is thrown if you are in a train scene, and you pass
                in <IL>useTrainMaterials</IL> as <IL>False</IL>. We do this
                because it guarantees every object can randomize to at least one
                material.
              </p>
            </>
          ),
        },
        {
          name: "useValMaterials",
          default: "None",
          type: "Optional[bool]",
          description: (
            <>
              <p>
                Specifies if the randomization should include materials found in
                validation rooms. Validation rooms consist of all of the
                following:
              </p>
              <ul>
                <li>
                  <Latex>{String.raw`Kitchens: $\text{FloorPlan}[21:25]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`Living Rooms: $\text{FloorPlan}[221:225]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`Bedrooms: $\text{FloorPlan}[321:325]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`Bathrooms: $\text{FloorPlan}[421:425]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`RoboTHOR Val: $\text{FloorPlan}\_\text{Val}*\_*$`}</Latex>
                </li>
              </ul>
              <p>
                If the agent is in one of the above scenes and <IL>None</IL> is
                passed in, then <IL>useValMaterials</IL> will default to{" "}
                <IL>True</IL>. Otherwise, it will default to <IL>False</IL>.
              </p>
              <p>
                An exception is thrown if you are in a validation scene, and you
                pass in <IL>useValMaterials</IL> as <IL>False</IL>. We do this
                because it guarantees every object can randomize to at least one
                material.
              </p>
            </>
          ),
        },
        {
          name: "useTestMaterials",
          default: "None",
          type: "Optional[bool]",
          description: (
            <>
              <p>
                Specifies if the randomization should include materials found in
                testing rooms. Testing rooms consist of all of the following:
              </p>
              <ul>
                <li>
                  <Latex>{String.raw`Kitchens: $\text{FloorPlan}[26:30]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`Living Rooms: $\text{FloorPlan}[226:230]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`Bedrooms: $\text{FloorPlan}[326:330]$`}</Latex>
                </li>
                <li>
                  <Latex>{String.raw`Bathrooms: $\text{FloorPlan}[426:430]$`}</Latex>
                </li>
              </ul>
              <p>
                If the agent is in one of the above scenes and <IL>None</IL> is
                passed in, then <IL>useTestMaterials</IL> will default to{" "}
                <IL>True</IL>. Otherwise, it will default to <IL>False</IL>.
              </p>
              <p>
                An exception is thrown if you are in a testing scene, and you
                pass in <IL>useTestMaterials</IL> as <IL>False</IL>. We do this
                because it guarantees every object can randomize to at least one
                material.
              </p>
            </>
          ),
        },
        {
          name: "inRoomTypes",
          default: "None",
          type: "List[str]",
          description: (
            <>
              <p>
                Specifies which room types the randomizable materials may come
                from.
              </p>
              <p>
                Valid room types include <IL>Kitchen</IL>, <IL>LivingRoom</IL>,{" "}
                <IL>Bedroom</IL>, <IL>Bathroom</IL>, and <IL>RoboTHOR</IL>.
                Casing is ignored. An exception is thrown if an unknown room
                type is passed in.
              </p>
              <p>
                If <IL>None</IL> is passed in, then all room types will be
                included.
              </p>
              <p>
                An exception is thrown if the current room type is not included
                in <IL>inRoomTypes</IL> and <IL>None</IL> is not passed in. We
                do this because it guarantees every object can randomize to at
                least one material.
              </p>
            </>
          ),
        },
      ],
    },

    RandomizeLighting: {
      argsTitle: "Randomize Lighting Parameters",
      description: <p></p>,
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="RandomizeLighting"
            parameters={parameters}
          />
          <Video
            id="552684045"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="10px"
          />
          <Demo />
        </>
      ),
      parameters: [
        {
          name: "brightness",
          default: "(0.5, 1.5)",
          type: "Tuple[float, float]",
          description: (
            <p>
              <Latex>
                Sets the bounds with which a light's intensity may be multiplied
                by. Higher values are brighter. Both values must be greater than
                $0$. The order of the values does not matter.
              </Latex>
            </p>
          ),
        },
        {
          name: "randomizeColor",
          default: "True",
          type: "bool",
          description: (
            <p>
              Specifies if the color should be randomized. If set to{" "}
              <IL>False</IL>, only each lights intensity may change.
            </p>
          ),
        },
        {
          name: "hue",
          default: "(0, 1)",
          type: "Tuple[float, float]",
          description: (
            <>
              <p>
                <Latex>
                  Sets the $(\min, \max)$ domain of possible hue values for each
                  light, which affects its color. Valid values are ranges
                  between $[0:1]$, where:
                </Latex>
              </p>
              <ul>
                <li>
                  <Latex>$0$ maps to a hue of $0$ degrees (i.e., red).</Latex>
                </li>
                <li>
                  <Latex>
                    $0.5$ maps to a hue of $180$ degrees (i.e., green).
                  </Latex>
                </li>
                <li>
                  <Latex>$1$ maps to a hue of $360$ degrees (i.e., red).</Latex>
                </li>
              </ul>
            </>
          ),
        },
        {
          name: "saturation",
          default: "(0.5, 1)",
          type: "Tuple[float, float]",
          description: (
            <p>
              <Latex>
                Sets the $(\min, \max)$ domain of possible saturation values for
                each light. Valid values are ranges between $[0:1]$, where $0$
                corresponds to grayscale and $1$ corresponds to full saturation.
              </Latex>
            </p>
          ),
        },
        {
          name: "synchronized",
          default: "False",
          type: "bool",
          description: (
            <p>
              Specifies if all lights in the room should be randomized with the
              same values (i.e., intensity and color). When <IL>False</IL>, each
              lighting object gets its own independent randomized intensity and
              randomized color.
            </p>
          ),
        },
      ],
    },

    RandomizeColors: {
      argsTitle: "Randomize Colors Description",
      description: <></>,
      example: (...parameters) => (
        <>
          <Code>controller.step(action="RandomizeColors")</Code>
          <Video
            id="552683978"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="10px"
          />
          <Demo />
        </>
      ),
      rightSide: (
        <>
          <p>
            Randomizes the color of each object in the scene. Compared to{" "}
            <IL>RandomizeMaterials</IL>, <IL>RandomizeColors</IL> looks much
            less realistic, but does enable a huge potential for visual
            variance.
          </p>
          <p>
            <Latex>
              Each color is uniformly sampled with a hue between $[0:1]$, a
              saturation in $[0.5:1]$, and a value in $[0.5:1]$.
            </Latex>
          </p>
        </>
      ),
    },

    AdvancePhysicsStep: {
      argsTitle: "Advance Physics Step Parameters",
      description: (
        <p>
          <IL>AdvancePhysicsStep</IL> responds with an Event after simulating
          the environment for <IL>timeStep</IL> seconds beyond the current
          state. Note that <IL>PausePhysicsAutoSim</IL> must be called first,
          before calling <IL>AdvancePhysicsStep</IL>.
        </p>
      ),
      example: (...parameters) => (
        <ActionExample
          actionName="AdvancePhysicsStep"
          parameters={parameters}
        />
      ),
      parameters: [
        {
          name: "timeStep",
          default: "0.01",
          type: "float",
          description: (
            <p>
              <Latex>
                The amount of time in seconds to advance the physics simulation.
                Valid values are in $$(0:0.05]$$.
              </Latex>
            </p>
          ),
        },
      ],
    },

    AddThirdPartyCamera: {
      argsTitle: "Add Third Party Camera Parameters",
      description: (
        <p>
          <IL>AddThirdPartyCamera</IL> adds an invisible camera to the scene,
          with images available for each successive event, until reset has been
          called. When reset is called, the camera is removed from the scene.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>
            {replaceParams(
              String.raw`event = controller.step(
    action="AddThirdPartyCamera",
    [params]
)

event.third_party_camera_frames`,
              parameters
            )}
          </Code>
        </>
      ),
      parameters: [
        {
          name: "position",
          required: true,
          example: "dict(x=-1.25, y=1, z=-1)",
          type: "dict[str, float]",
          description: (
            <p>
              <Latex>
                The global $$(x, y, z)$$ position of where the camera will be
                placed.
              </Latex>
            </p>
          ),
        },
        {
          name: "rotation",
          required: true,
          example: "dict(x=90, y=0, z=0)",
          type: "dict[str, float]",
          description: (
            <p>
              <Latex>
                The global $$(x, y, z)$$ rotation of where the camera.
              </Latex>
            </p>
          ),
        },
        {
          name: "fieldOfView",
          default: "90",
          type: "dict[str, float]",
          description: (
            <p>
              <Latex>
                Changes the camera's optical field of view, in degrees. Valid
                values are in the domain $$(0:180)$$.
              </Latex>
            </p>
          ),
        },
      ],
    },

    AddThirdPartyCameraResponse: {
      argsTitle: "Third Party Camera Response",
      description: (
        <p>
          Image frames are then accessible with the{" "}
          <IL>third_party_camera_frames</IL> attribute on successive events:
        </p>
      ),
      example: (...parameters) => (
        <Response
          lines={[
            "<ai2thor.server.Event",
            "    .third_party_camera_frames",
            "",
            "    {...}",
            "/>",
          ]}
        />
      ),
      parameters: [
        {
          name: "third_party_camera_frames",
          type: "list[numpy.ndarray]",
          description: (
            <>
              <p>
                A list of RGB frames from rendered from each third party camera.
                Images are stored as numpy.uint8 ndarrays of size (height,
                width, 3), where the height and width are the same as the
                agent's egocentric image height and width.
              </p>
              <p>
                Camera frames in the list appear in the order that they were
                added to the scene.
              </p>
            </>
          ),
        },
      ],
    },

    UpdateThirdPartyCamera: {
      argsTitle: "Update Third Party Camera Parameters",
      description: (
        <p>
          <IL>UpdateThirdPartyCamera</IL> updates the state of a previously
          added third party camera that is currently in the scene. Any values
          that are unspecified will remain the same.
        </p>
      ),
      example: (...parameters) => (
        <ActionExample
          actionName="UpdateThirdPartyCamera"
          parameters={parameters}
        />
      ),
      parameters: [
        {
          name: "thirdPartyCameraId",
          default: "0",
          type: "int",
          description: (
            <>
              <p>
                Targets which third party camera to modify. Third party camera
                ids are based on the order that they were added to the scene.
              </p>
              <p>
                <Latex>
                  Valid values are in $$[0:\text&#123;len&#125;(\text&#123;third
                  party cameras&#125;) - 1]$$.
                </Latex>
              </p>
            </>
          ),
        },
        {
          name: "position",
          example: "dict(x=-1.25, y=1, z=-1)",
          type: "dict[str, float]",
          description: (
            <p>
              <Latex>
                The global $$(x, y, z)$$ position of where the camera will be
                placed.
              </Latex>
            </p>
          ),
        },
        {
          name: "rotation",
          example: "dict(x=90, y=0, z=0)",
          type: "dict[str, float]",
          description: (
            <p>
              <Latex>
                The global $$(x, y, z)$$ rotation of how the camera will be
                orientated.
              </Latex>
            </p>
          ),
        },
        {
          name: "fieldOfView",
          example: "90",
          type: "dict[str, float]",
          description: (
            <p>
              <Latex>
                Changes the camera's optical field of view, in degrees. Valid
                values are in the domain $$(0:180)$$.
              </Latex>
            </p>
          ),
        },
      ],
    },

    GetObjectInFrame: {
      argsTitle: "Get Coordinate from Raycast Parameters",
      description: (
        <>
          <p>
            <IL>GetObjectInFrame</IL> queries the current view of the agent for
            the object that appears at specified <Latex>$$(x, y)$$</Latex>{" "}
            coordinate, relative to its current view. If there is an object that
            appears at the coordinate, its objectId is provided in{" "}
            <IL>query.metadata["actionReturn"]</IL>. Alternatively, if no object
            is at the provided coordinate,{" "}
            <IL>query.metadata["actionReturn"]</IL> will be <IL>None</IL> and{" "}
            <IL>bool(query)</IL> will be <IL>False</IL>.
          </p>
          <p>
            This action can be used in tandem with object interaction actions,
            where <IL>GetObjectInFrame</IL> is first called to extract an{" "}
            <IL>objectId</IL>, and if the objectId is extracted.
          </p>
        </>
      ),
      example: (...parameters) => (
        <>
          <Code>
            {replaceParams(
              String.raw`query = controller.step(
    action="GetObjectInFrame",
    [params]
)

object_id = query.metadata["actionReturn"]`,
              parameters
            )}
          </Code>
          <Img
            fluid={parameters[4].imageCoordinates.childImageSharp.fluid}
            css={css`
              margin-top: 15px;
            `}
          />
        </>
      ),
      parameters: [
        {
          name: "x",
          required: true,
          type: "float",
          example: "0.64",
          description: (
            <p>
              <Latex>
                The $$x$$ coordinate from the current image frame, corresponding
                to the relative distance from the left of the frame. Valid
                values are in $$[0:1]$$.
              </Latex>
            </p>
          ),
        },
        {
          name: "y",
          required: true,
          type: "float",
          example: "0.40",
          description: (
            <p>
              <Latex>
                The $$y$$ coordinate from the current image frame, corresponding
                to the relative distance from the top of the frame. Valid values
                are in $$[0:1]$$.
              </Latex>
            </p>
          ),
        },
        {
          name: "checkVisible",
          default: "False",
          type: "bool",
          description: (
            <p>
              If <IL>False</IL>, the returned object will only be provided if it
              is within a distance of the initialized{" "}
              <IL>visibilityDistance</IL> (default: 1.5 meters) from the agent.
              This is set to <IL>False</IL> by default so that the agent can
              only interact with objects in-front of it, rather than objects far
              across the room.
            </p>
          ),
        },
      ],
    },

    GetCoordinateFromRaycast: {
      argsTitle: "Get Coordinate from Raycast Parameters",
      description: (
        <p>
          <IL>GetCoordinateFromRaycast</IL> sends a raycast out from the camera
          in the direction of the <Latex>$$(x, y)$$</Latex> screen coordinate,
          relative to the agent's current view. The world{" "}
          <Latex>$$(x, y, z)$$</Latex> coordinate of the first point of
          collision that is hit on an object by the raycast is returned in{" "}
          <IL>query.metadata["actionReturn"]</IL>.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>
            {replaceParams(
              String.raw`query = controller.step(
    action="GetCoordinateFromRaycast",
    [params]
)

coordinate = query.metadata["actionReturn"]`,
              parameters
            )}
          </Code>
          <Img
            fluid={parameters[4].imageCoordinates.childImageSharp.fluid}
            css={css`
              margin-top: 15px;
            `}
          />
        </>
      ),
      parameters: [
        {
          name: "x",
          required: true,
          type: "float",
          example: "0.64",
          description: (
            <p>
              <Latex>
                The $$x$$ coordinate from the current image frame, corresponding
                to the relative distance from the left of the frame. Valid
                values are in $$[0:1]$$.
              </Latex>
            </p>
          ),
        },
        {
          name: "y",
          required: true,
          type: "float",
          example: "0.40",
          description: (
            <p>
              <Latex>
                The $$y$$ coordinate from the current image frame, corresponding
                to the relative distance from the top of the frame. Valid values
                are in $$[0:1]$$.
              </Latex>
            </p>
          ),
        },
      ],
    },

    GetReachablePositions: {
      argsTitle: "Get Reachable Positions Response",
      description: (
        <p>
          <IL>GetReachablePositions</IL> finds all the positions that the agent
          can reach in a scene. It does an optimized BFS over a grid spaced out
          by the initialized <IL>gridSize</IL>. The valid positions are then
          added and returned in a list. The action can be used in tandem with
          Teleport, to actually travel to a given position.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code
            lines={[
              "positions = controller.step(",
              '    action="GetReachablePositions"',
              ').metadata["actionReturn"]',
            ]}
          />
          <Response
            lines={[
              "[",
              "    dict(x=(...), y=(...), z=(...)),",
              "    dict(x=(...), y=(...), z=(...)),",
              "    dict(x=(...), y=(...), z=(...)),",
              "    {...}",
              "    dict(x=(...), y=(...), z=(...)),",
              "]",
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "actionReturn",
          type: "list[dict[str, float]]",
          description: (
            <p>
              <Latex>
                A list of $$(x, y, z)$$ positions that the agent can reach in
                the scene.
              </Latex>
            </p>
          ),
        },
      ],
    },
  },

  ArmAgent: {
    initialization: {
      argsTitle: "Controller Parameters",
      description: (
        <p>
          After installing AI2-THOR, we can initialize a controller, which will
          allow us to execute actions in the environment.
        </p>
      ),
      example: (...parameters) => (
        <Code>
          {replaceParams(
            String.raw`from ai2thor.controller import Controller

controller = Controller(
    [params]
)`,
            parameters
          )}
        </Code>
      ),
      parameters: [
        {
          name: "agentMode",
          required: true,
          type: "str",
          example: '"arm"',
          description: (
            <div>
              <p>
                Always set this to <IL>arm</IL> in order to use the agent
                designed for ManipulaTHOR.
              </p>
              <Warning>
                If unset, the agent will be the default iTHOR agent, which is
                not the agent designed for ManipulaTHOR. Thus, the agent will
                have different properties than these docs.
              </Warning>
              <p>
                A RuntimeError is raised if <IL>agentMode</IL> is not set to
                "default", "locobot", "drone", or "arm".
              </p>
            </div>
          ),
        },
        {
          name: "massThreshold",
          description: (
            <>
              <p>
                Blocks the arm from being able to directly interact and move
                objects with a mass above <IL>massThreshold</IL>. Empirically,
                it may help make the environment more deterministic.
              </p>
              <Remark>
                This only stops the arm from knocking over things above the
                threshold that it directly touches. For example, if the arm
                touches an object with a mass below <IL>massThreshold</IL> that
                is adjacent to an object with a mass above{" "}
                <IL>massThreshold</IL>, pushing the smaller object can still
                cause the larger object to move since no direct contact was
                made.
              </Remark>
            </>
          ),
          type: "Optional[float]",
          default: "None",
          // example: "None",
        },
        {
          name: "scene",
          description: (
            <p>
              The name of which scene to initialize. ManipulaTHOR is currently
              set up to work with iTHOR scenes. All iTHOR scene names and
              descriptions can be found{" "}
              <Link to="/ithor/documentation/scenes">here</Link>.
            </p>
          ),
          type: "Optional[str]",
          default: "None",
          example: '"FloorPlan203"',
        },
        {
          name: "visibilityDistance",
          description: (
            <p>
              Used to set the maximum goal distance in meters between the agent
              and an object. This affects each object's visible property, where
              when True, the agent is within <IL>visibilityDistance</IL> of that
              object and the object appears in the agent's current egocentric
              frame.
            </p>
          ),
          type: "float",
          default: "1.5",
        },
        {
          name: "gridSize",
          type: "float",
          description: (
            <p>
              With the ManipulaTHOR agent, this only affects how far positions
              are spaced with <IL>GetReachablePositions</IL>.
            </p>
          ),
          default: "0.25",
        },
        {
          name: "renderDepthImage",
          type: "bool",
          default: "False",
          description: (
            <>
              <p>
                When <IL>True</IL>, a depth frame is rendered and made available
                as an <Link to="#event-depth_frame">event.depth_frame</Link>{" "}
                attribute.
              </p>
              <p>
                We require this to be explicitly passed in because rendering
                depth takes longer than rendering RGB alone.
              </p>
            </>
          ),
        },
        {
          name: "renderInstanceSegmentation",
          type: "bool",
          default: "False",
          description: (
            <>
              <p>
                When <IL>True</IL>, a instance segmentation frame is rendered
                and made available as an{" "}
                <IL>Event.instance_segmentation_frame</IL> attribute. Instance
                segmentation provides segmentation by object instance, where all
                objects in view are distinguishable.
              </p>
              <p>
                We require this to be explicitly passed in because rendering
                instance segmentation takes longer than rendering RGB alone.
              </p>
            </>
          ),
        },
        // {
        //   name: "renderSemanticSegmentation",
        //   type: "bool",
        //   default: "False",
        //   description: (
        //     <>
        //       <p>
        //         When <IL>True</IL>, a instance segmentation frame is rendered
        //         and made available as an{" "}
        //         <IL>Event.instance_segmentation_frame</IL> attribute. Instance
        //         segmentation provides segmentation by object instance, where all
        //         objects in view are distinguishable.
        //       </p>
        //       <p>
        //         We require this to be explicitly passed in because rendering
        //         instance segmentation takes longer than rendering RGB alone.
        //       </p>
        //     </>
        //   ),
        // },
        {
          name: "width",
          type: "int",
          default: "300",
          description: (
            <p>
              The number of horizontal sampled pixels for each frame. This
              affects every rendered image frame (e.g., RGB, depth, BGR).
            </p>
          ),
        },
        {
          name: "height",
          type: "int",
          default: "300",
          description: (
            <p>
              The number of vertical sampled pixels for each frame. This affects
              every rendered image frame (e.g., RGB, depth, BGR).
            </p>
          ),
        },
        {
          name: "fieldOfView",
          type: "int",
          default: "60",
          description: (
            <>
              <p>
                Changes the camera's optical field of view, in degrees. Valid
                values are in the domain <Latex>$$(0:180)$$</Latex>. The default
                field of view for the LoCoBot is 60 degrees.
              </p>
              <Remark>
                <p>
                  Not all agent modes have the same default field of view. For
                  instance, the drone's field of view is 150 degrees, by
                  default.
                </p>
              </Remark>
            </>
          ),
        },
      ],
    },

    MoveAgent: {
      argsTitle: "Move Agent Parameters",
      description: (
        <p>
          <IL>MoveAgent</IL> moves the agent's body from it's current facing
          direction. Compared to iTHOR move actions (e.g., <IL>MoveAhead</IL>
          ), <IL>MoveAgent</IL> can be used to move the agent diagonally (e.g.,
          move ahead and left at the same time).
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample actionName="MoveAgent" parameters={parameters} />
          <p
            css={css`
              margin-top: 10px;
            `}
          >
            <Latex>
              The video belows shows the result of consecutively calling the
              action with (ahead, right) $$= (0.25, 0.0)\to (-0.5, 0.0)\to (0.0,
              0.25)\to (0.0, -0.5)\to (0.5, 0.5)$$.
            </Latex>
          </p>
          <Video id="540018939" />
        </>
      ),
      parameters: [
        {
          name: "ahead",
          default: "0",
          type: "float",
          example: "0.25",
          description: (
            <>
              <p>
                The distance, in meters, that the agent attempts to move in its
                forward facing direction.
              </p>
              <Remark>Use negative values to move the agent backwards.</Remark>
            </>
          ),
        },
        {
          name: "right",
          default: "0",
          type: "float",
          example: "0.25",
          description: (
            <>
              <p>
                The distance, in meters, that the agent attempts to move
                rightward from its forward facing direction.
              </p>
              <Remark>Use negative values to move the agent leftwards.</Remark>
            </>
          ),
        },
        {
          name: "returnToStart",
          default: "True",
          type: "bool",
          description: (
            <p>
              Sets the agent back to its starting pose if it collides with an
              object while attempting to move.
            </p>
          ),
        },
        {
          name: "speed",
          default: "1",
          type: "float",
          description: <p>How fast the agent moves, in meters per second.</p>,
        },
        {
          name: "fixedDeltaTime",
          default: "0",
          type: "float",
          example: "0.02",
          description: (
            <>
              <p>
                The interval, in seconds, with which that physics time steps are
                simulated.
              </p>
              <p>
                For instance, if the interval is large, then the agent may move
                to the location in a manner that looks like teleportation. But,
                then there may not be many checks along the path to see if the
                agent collides with an object as it moves.
              </p>
            </>
          ),
        },
      ],
    },

    RotateAgent: {
      argsTitle: "Rotate Agent Parameters",
      description: (
        <p>
          <IL>RotateAgent</IL> rotates the agent's body from it's current facing
          direction.
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample actionName="RotateAgent" parameters={parameters} />
          <p
            css={css`
              margin-top: 10px;
            `}
          >
            <Latex>
              The video belows shows the result of consecutively the action with
              degrees $$= 60\to -120$$.
            </Latex>
          </p>
          <Video id="540022866" />
        </>
      ),
      parameters: [
        {
          name: "degrees",
          type: "float",
          required: true,
          example: "30",
          description: (
            <>
              <p>
                Amount to rotate the agent <b>rightward</b> of its current
                facing direction.
              </p>
              <Remark>
                Use negative values to rotate the agent leftwards.
              </Remark>
            </>
          ),
        },
        {
          name: "returnToStart",
          default: "True",
          type: "bool",
          description: (
            <p>
              Sets the agent back to its starting pose if it collides with an
              object while attempting to move.
            </p>
          ),
        },
        {
          name: "speed",
          default: "1",
          type: "float",
          description: <p>How fast the agent moves, in degrees per second.</p>,
        },
        {
          name: "fixedDeltaTime",
          default: "0",
          type: "float",
          example: "0.02",
          description: (
            <>
              <p>
                The interval, in seconds, with which physics time steps are
                simulated.
              </p>
              <p>
                If the interval is large, for instance, then the agent may move
                to the location in a manner that looks like teleportation. But,
                then there may not be many checks along the path to see if the
                agent were to collide with an object as it moved.
              </p>
            </>
          ),
        },
      ],
    },

    MoveArmBase: {
      argsTitle: "Move Arm Base Parameters",
      description: (
        <p>
          Raise and lower the height of the entire arm. This height is relative
          to the minimum and maximum extents, relative to the agent's size.
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample actionName="MoveArmBase" parameters={parameters} />
          <Img
            fluid={parameters[4].moveArmBase.childImageSharp.fluid}
            css={css`
              margin-top: 15px;
            `}
          />
        </>
      ),
      parameters: [
        {
          name: "y",
          type: "float",
          required: true,
          example: "0.5",
          description: (
            <>
              <p>
                <Latex>
                  The height of the arm. Value values are in $$[0:1]$$, where
                  $$0$$ is the lowest possible height of the arm and $$1$$ is
                  the largest hight of the arm.
                </Latex>
              </p>
              <p>
                The video below displays the arm base <IL>y</IL> moving between{" "}
                <Latex>$$0.5 \to 1.0\to 0.0\to 0.5\to 0.75\to 0.25$$</Latex> (in
                that order).
              </p>
              {/* <Video id="540003636" /> */}
            </>
          ),
        },
        {
          name: "speed",
          default: "1",
          type: "float",
          description: <p>How fast the arm moves, in meters per second.</p>,
        },
        {
          name: "returnToStart",
          default: "True",
          type: "bool",
          description: (
            <p>
              Sets the agent's arm base position back to its starting <IL>y</IL>{" "}
              coordinate if it collides with an object while attempting to move.
            </p>
          ),
        },
        {
          name: "fixedDeltaTime",
          default: "0",
          type: "float",
          example: "0.02",
          description: (
            <>
              <p>
                The interval, in seconds, with which physics time steps are
                simulated.
              </p>
              <p>
                If the interval is large, for instance, then the agent's arm may
                move into position in a manner that looks like teleportation.
                But, then there may not be many checks along the path to see if
                the arm were to collide with an object as it moved.
              </p>
            </>
          ),
        },
      ],
    },

    PickupObject: {
      argsTitle: "Pickup Object Parameters",
      description: (
        <p>
          Picks up all pickupable sim objects that are within the agent's hand
          sphere radius.
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample actionName="PickupObject" parameters={parameters} />
          <Img
            fluid={parameters[4].pickupObject.childImageSharp.fluid}
            css={css`
              margin-top: 15px;
            `}
          />
        </>
      ),
      parameters: [
        {
          name: "objectIdCandidates",
          type: "list[str]",
          example: '["Apple|1|1|0", "Fork|2|1|1"]',
          default: "[]",
          description: (
            <p>
              Restricts the arm to only being able to pickup these specific
              objects. These objects will only be picked up iff they are within
              the agent's hand sphere radius.
            </p>
          ),
        },
      ],
    },

    MoveArm: {
      argsTitle: "Pickup Object Parameters",
      description: (
        <p>
          Move the wrist joint of the Mid-Level-Arm to position coordinate.
          Position coordinates are relative to the arm’s forward direction. This
          means using (0, 0, 1) will extend the arm straight forward in the
          arm’s positive z direction. Note that the coordinates can also be
          passed in relative to the camera on the agent’s hand. See the
          handCameraSpace parameter below for more info.
        </p>
      ),
      example: (...parameters) => (
        <ActionExample actionName="MoveArm" parameters={parameters} />
      ),
      parameters: [
        {
          name: "position",
          type: "Dict[str, float]",
          example: "dict(x=0, y=0.5, z=0)",
          required: true,
          description: (
            <>
              <p>
                The position of where to move the arm. See the{" "}
                <IL>coordinateSpace</IL> parameter for how the position is
                interpreted. All positions are given in terms of meters.
              </p>
            </>
          ),
        },
        {
          name: "coordinateSpace",
          type: "str",
          default: '"armBase"',
          description: (
            <>
              <p>
                Defines how the <IL>position</IL> parameter should be
                interpreted. Valid values are:
              </p>
              <ul>
                <li>
                  <IL>armBase</IL> moves the arm in the coordinate system
                  relative to the spot where the spot where the arm's "shoulder"
                  is being held by the base.
                  <br
                    css={css`
                      margin-bottom: 12px;
                    `}
                  />
                  Analogous to <IL>Teleport</IL>, using the same coordinate
                  global space, but with the arm. For instance, one may use
                  object metadata to move the arm near an object's global
                  position.
                  <br
                    css={css`
                      margin-bottom: 12px;
                    `}
                  />
                  After initializing a scene, the arm starts at{" "}
                  <Latex>$$(x=0, y=0, z=0.5)$$</Latex>, relative to the{" "}
                  <IL>armBase</IL>.
                  <br
                    css={css`
                      margin-bottom: 12px;
                    `}
                  />
                  The video below shows many examples of <IL>moveArm</IL> with{" "}
                  <IL>armBase</IL> as the <IL>coordinateSpace</IL>. Notice:{" "}
                  <br
                    css={css`
                      margin-bottom: 8px;
                    `}
                  />
                  <ul>
                    <li>
                      <Latex>
                        $$x$$ corresponds to the left and right direction (with
                        $$x=0$$ in the middle).
                      </Latex>
                    </li>
                    <li>
                      <Latex>
                        $$y$$ corresponds to the vertical direction (with
                        $$y=0$$ being the middle).
                      </Latex>
                    </li>
                    <li>
                      <Latex>
                        $$z$$ corresponds to the distance from the agent, with
                        $$z=0$$ being next to the agent, and $$z=0.75$$ being
                        $$0.75$$ meters in front of the agent.
                      </Latex>
                    </li>
                  </ul>
                  <Video id="540031293" />
                </li>
                <li>
                  <IL>wrist</IL> moves the arm relative to its current position
                  and rotation.
                  <br
                    css={css`
                      margin-bottom: 12px;
                    `}
                  />
                  Analogous to an action like <IL>MoveAgent</IL>, where calling
                  it consecutively attempts to keep moving the agent to a new
                  position, whereas calling <IL>Teleport</IL> consecutively will
                  never change the goal position, after the first call.
                  <br
                    css={css`
                      margin-bottom: 12px;
                    `}
                  />
                  The video below shows the arm starting at <IL>armBase</IL>{" "}
                  coordinate <Latex>$$(x=0.5, y=-0.5, z=0.5)$$</Latex> and
                  consecutively calling <IL>MoveArm</IL> with{" "}
                  <Latex>$$(x=-0.2, y=0.2, z=0)$$</Latex>. Here, notice that
                  since the position is relative to the agent's current arm
                  position, the arm continues to move after passing in the same
                  position consecutively.
                  <Video id="540044934" />
                </li>
                <li>
                  <IL>world</IL> moves the arm to a position in global, world
                  space.
                  <br
                    css={css`
                      margin-bottom: 8px;
                    `}
                  />
                  Analogous to <IL>Teleport</IL>, using the same coordinate
                  global space, but with the arm. For instance, one may use
                  object metadata to move the arm near an object's global
                  position.
                </li>
              </ul>
            </>
          ),
        },
        {
          name: "restrictMovement",
          default: "False",
          type: "bool",
          description: (
            <>
              <p>
                Restricts the arm to only being able to reach certain positions
                that are realistic. If <IL>False</IL>, the agent will be able to
                reach behind itself and clipping into itself is ignored.
              </p>
              <p>
                The video below shows an example where <IL>restrictMovement</IL>{" "}
                is <IL>False</IL>, which allows the agent to reach behind
                itself.
              </p>
              {/* <Video id="540053732" /> */}
            </>
          ),
        },
        {
          name: "speed",
          default: "1",
          type: "float",
          description: <p>How fast the arm moves, in meters per second.</p>,
        },
        {
          name: "returnToStart",
          default: "True",
          type: "bool",
          description: (
            <p>
              Reverts the arm's position to its pose before the action was
              called, if the arm collides with an object while attempting to
              move it into position.
            </p>
          ),
        },
        {
          name: "fixedDeltaTime",
          default: "0",
          type: "float",
          example: "0.02",
          description: (
            <>
              <p>
                The interval, in seconds, with which that physics time steps are
                simulated.
              </p>
              <p>
                For instance, if the interval is large, then the agent's arm may
                move to the location in a manner that looks like teleportation.
                But, then there may not be many checks along the path to see if
                the agent's arm collides with an object as it moves.
              </p>
            </>
          ),
        },
      ],
    },

    ReleaseObject: {
      argsTitle: "Release Object Parameters",
      description: (
        <p>Drops any objects that the agent is currently holding.</p>
      ),
      example: (...parameters) => 'controller.step(action="ReleaseObject")',
      parameters: [],
    },

    SetHandSphereRadius: {
      argsTitle: "Parameters",
      description: (
        <p>
          Changes the radius of the agent's "magnet sphere" hand. Here, if a
          pickupable object is within <IL>radius</IL> of the agent's hand and{" "}
          <IL>PickupObject</IL> is called, the object will be picked up.
        </p>
      ),
      example: (...parameters) => (
        <ActionExample
          actionName="SetHandSphereRadius"
          parameters={parameters}
        />
      ),
      parameters: [
        {
          name: "radius",
          type: "float",
          required: true,
          example: "0.1",
          description: (
            <p>
              <Latex>
                The radius on the agent's "magnet sphere" hand, in meters. Valid
                values are in $$(0.04:0.5)$$ meters.
              </Latex>
            </p>
          ),
        },
      ],
    },
  },

  PhysicsAgent: {
    MoveAhead: {
      argsTitle: "Movement Parameters",
      description: (
        <p>
          Move the agent in a cardinal direction, relative to it's forward
          facing direction. Supported action directions are <IL>MoveAhead</IL>,{" "}
          <IL>MoveBack</IL>, <IL>MoveLeft</IL>, and <IL>MoveRight</IL>.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>
            {replaceParams(
              String.raw`controller.step(
    action="MoveAhead",
    [params]
)

# Other supported directions
controller.step("MoveBack")
controller.step("MoveLeft")
controller.step("MoveRight")`,
              parameters
            )}
          </Code>
          <Video
            id="553028619"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        {
          name: "moveMagnitude",
          default: "None",
          type: "Optional[float]",
          description: (
            <p>
              Overrides the initialized step size of <IL>gridSize</IL>. The
              units are specified in meters. By default, the <IL>gridSize</IL>{" "}
              is initialized to 0.25 meters.
            </p>
          ),
        },
      ],
    },

    RotateRight: {
      argsTitle: "Rotate Parameters",
      description: (
        <p>
          Changes the yaw rotation of the agent's body. Supported action
          directions are <IL>RotateRight</IL> and <IL>RotateLeft</IL>.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>
            {replaceParams(
              String.raw`controller.step(
    action="RotateRight",
    [params]
)

# Other supported direction
controller.step("RotateLeft")`,
              parameters
            )}
          </Code>
          <Video
            id="553035079"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        {
          name: "degrees",
          default: "None",
          type: "Optional[float]",
          description: (
            <p>
              Overrides the initialized rotation amount of{" "}
              <IL>rotateStepDegrees</IL>.
            </p>
          ),
        },
      ],
    },

    LookUp: {
      argsTitle: "Camera Rotation Parameters",
      description: (
        <p>
          We can adjust the camera's pitch by utilizing the <IL>LookUp</IL> and{" "}
          <IL>LookDown</IL> commands. Both increment in{" "}
          <Latex>$$30^\circ$$</Latex> intervals, with angles being clamped
          between <Latex>$$60^\circ$$</Latex> in the downward direction and{" "}
          <Latex>$$30^\circ$$</Latex> in the upward direction.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>
            {replaceParams(
              String.raw`controller.step(
    action="LookUp",
    [params]
)

# Other supported direction
controller.step("LookDown")`,
              parameters
            )}
          </Code>
          <Video
            id="553132883"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        {
          name: "degrees",
          default: "30",
          type: "float",
          description: (
            <p>
              <Latex>
                Specifies the number of degrees to look up or down. Must be
                greater than $0$.
              </Latex>
            </p>
          ),
        },
      ],
    },

    GetInteractablePoses: {
      argsTitle: "Get Interactable Poses Attributes",
      description: (
        <>
          <p>
            <IL>GetInteractablePoses</IL> returns all the agent poses where an
            object is <IL>visible</IL> to the agent. A pose assigns every degree
            of freedom on the agent to a specific value. A pose can then be
            passed into TeleportFull to teleport the agent to a given pose.
          </p>
          <Warning>
            <p>
              In order for an object to be <IL>visible</IL> at a certain pose,
              the object has to both be within <IL>visibilityDistance</IL> of
              the agent's camera and it must be in the agent's field of view.
              Thus, if the agent is too far away from an object, but it appears
              in the agent's field of view, the object will return{" "}
              <IL>visible</IL> as <IL>False</IL>.
            </p>
          </Warning>
          <p>
            The action also provides the ability to restrict which poses should
            be searched, for each degree of freedom. For instance, one can
            restrict the poses they want to poses where <IL>horizon</IL> is{" "}
            <IL>True</IL> and <IL>standing</IL> is <IL>True</IL>. Such
            restrictions can often make the action execute faster, if the search
            space is more constrained.
          </p>
          <Warning>
            <p>
              This action is expected to solely be used in tandem with
              TeleportFull. Hence, in future releases, if the agent is given
              extra degrees of freedom, those degrees of freedom will be
              included in each pose, and thus change the returned poses.
              Therefore, we recommend not indexing into any pose (i.e.,{" "}
              <IL>pose["horizon"]</IL>
              ), and instead passing the entire pose to TeleportFull using
              Python's <IL>**kwargs</IL> feature (i.e., <IL>**pose</IL>).
            </p>
          </Warning>
        </>
      ),
      example: (...parameters) => (
        <>
          <Code>
            {replaceParams(
              String.raw`import numpy as np

event = controller.step(
    action="GetInteractablePoses",
    [params]
)

poses = event.metadata["actionReturn"]`,
              parameters
            )}
          </Code>
          <Response
            lines={[
              "[",
              "    dict(",
              "       x=(...),",
              "       y=(...),",
              "       z=(...),",
              "       horizon=(...),",
              "       rotation=(...),",
              "       standing=(...)",
              "    ),",
              "    {...},",
              "    dict(",
              "       x=(...),",
              "       y=(...),",
              "       z=(...),",
              "       horizon=(...),",
              "       rotation=(...),",
              "       standing=(...)",
              "    )",
              "]",
            ]}
          />
          <Code
            caption="TeleportFull to a Pose"
            lines={[
              "import random",
              "pose = random.choice(poses)",
              "",
              'controller.step("TeleportFull", **pose)',
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "objectId",
          type: "str",
          required: true,
          example: `"Apple|-1.0|+1.0|+1.5"`,
          description: (
            <p>
              The objectId of the object with which the interactable poses will
              be queried.
            </p>
          ),
        },
        {
          name: "positions",
          type: "Optional[list[dict[str, float]]]",
          default: "None",
          example: `[dict(x=0, y=0.9, z=0)]`,
          description: (
            <p>
              Restricts which positions should be searched. If not specified,
              all positions from <IL>GetReachablePositions</IL> will be used.
            </p>
          ),
        },
        {
          name: "rotations",
          type: "Optional[list[float]]",
          default: "None",
          example: `range(0, 360, 10)`,
          description: (
            <>
              <p>
                Restricts which rotation values should appear in the returned
                response. For instance, if <IL>[0, 180]</IL> is passed in, only
                such values may appear as the rotation for each returned pose.
                By default, the rotation values are
              </p>
              <Latex displayMode={true}>
                {String.raw`$$\text{range}(A_r\,\%\, D,\;\; D,\;\; 360 + A_r\,\%\, D),$$`}
              </Latex>
              <p>
                where{" "}
                <Latex>
                  $$A_r$$ is the current rotation of the agent and $$D$$ is the
                  initialized
                </Latex>{" "}
                <IL>rotateStepDegrees</IL>. For instance, if{" "}
                <Latex>$$A_r = 10^\circ$$ and $$D = 90$$</Latex>, then the
                default rotations will be <IL>[10, 100, 190, 280]</IL>.
              </p>
              <Warning>
                <p>
                  An exception is thrown if 360 mod the initialized{" "}
                  <IL>rotateStepDegrees</IL> does not equal 0 and rotations has
                  not been provided. Here, the agent cannot rotate in a circular
                  manner, and hence, an infinite number of rotations would be
                  possible.
                </p>
              </Warning>
            </>
          ),
        },
        {
          name: "horizons",
          type: "Optional[list[float]]",
          default: "None",
          example: `np.linspace(-30, 60, 30)`,
          description: (
            <>
              <p>
                Restricts which horizons should be searched. For instance, if{" "}
                <IL>[0, 15]</IL> is passed in, only such values may appear as
                the horizon for each returned pose. Defaults to using{" "}
                <IL>[-30, 0, 30, 60]</IL>.
              </p>
              <Warning>
                <p>
                  <Latex>Each horizon must be in $$[-30:60]$$.</Latex>
                </p>
              </Warning>
            </>
          ),
        },
        {
          name: "standings",
          type: "Optional[list[bool]]",
          default: "None",
          example: `[True, False]`,
          description: (
            <p>
              Restricts which <IL>standing</IL> poses should be added to the
              search. For instance, if <IL>[True]</IL> is passed in, only values
              of <IL>standing=True</IL> may appear in the response. Defaults to{" "}
              <IL>[True, False]</IL>.
            </p>
          ),
        },
      ],
    },
    PickupObject: {
      argsTitle: "Pickup Object Parameters",
      description: (
        <>
          <p>
            <IL>PickupObject</IL> allows the agent to pick up an interactable
            object specified by its objectId. Compatible objects have{" "}
            <IL>pickupable</IL> as <IL>True</IL> in their object metadata and
            return their current state with <IL>isPickedUp</IL>. Note that the
            agent’s hand must be clear of obstruction or the action will fail.
            If the target object being in the agent’s hand would cause it to
            clip into the environment, the action will also fail.
          </p>
          <p>
            Picked up objects can also obstruct the Agent’s view of the
            environment since the Agent’s hand is always in camera view, so know
            that picking up larger objects will obstruct the field of vision.
          </p>
          <Warning>
            <p>
              Certain objects are <IL>receptacles</IL> that can themselves be
              picked up. If a moveable receptacle is picked up while other Sim
              Objects are inside of it, the contained objects will be picked up
              with the moveable receptacle. Here, a sequences like “
              <IL>Place</IL> the <IL>Egg</IL> on the <IL>Plate</IL>, then to{" "}
              <IL>PickUp</IL> the <IL>Plate</IL>” would <IL>PickUp</IL> the{" "}
              <IL>Plate</IL> with <IL>Egg</IL> on it.
            </p>
          </Warning>
        </>
      ),
      example: (...parameters) => (
        <>
          <ActionExample actionName="PickupObject" parameters={parameters} />
          <Video
            id="552988478"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        objectId(),
        {
          name: "forceAction",
          default: "False",
          type: "bool",
          description: (
            <p>
              Set to <IL>True</IL> to allow an object to be picked up regardless
              of if it is within the <IL>visibilityDistance</IL>of the agent.
            </p>
          ),
        },
        {
          name: "manualInteract",
          default: "False",
          type: "bool",
          description: (
            <p>
              By default, objects picked up by the agent teleport into the
              agent’s hand at a default position in front of the agent camera.
              Set this to <IL>True</IL> in order to instead pick up an object at
              the object’s location. This allows the agent to manipulate the
              object via object manipulation actions without the abstraction of
              the picked up object teleporting to the agent’s hand.
            </p>
          ),
        },
      ],
    },
    PutObject: {
      description: (
        <p>
          <IL>PutObject</IL> attempts to put an object the agent is holding onto
          or into the target receptacle. Valid target receptacle objects have{" "}
          <IL>receptacle</IL> as <IL>True</IL> in their object metadata.
        </p>
      ),
      argsTitle: "Put Object Parameters",
      example: (...parameters) => (
        <>
          <ActionExample actionName="PutObject" parameters={parameters} />
          <Video id="553872349" marginTop="10px" marginBottom="0px" />
        </>
      ),
      parameters: [
        objectId(),
        {
          name: "forceAction",
          type: "bool",
          default: "False",
          description: (
            <p>
              Enable to ignore any Receptacle Restrictions when attempting to
              place objects. Normally objects will fail to be put on a
              receptacle if that receptacle is not valid for the object. This
              will also ignore interaction range restrictions of the agent. Note
              this does not guarantee an object will be placed in a receptacle,
              as some objects will not fit inside a receptacle regardless of the
              default object restrictions.
            </p>
          ),
        },
        {
          name: "placeStationary",
          type: "bool",
          default: "True",
          description: (
            <p>
              If <IL>False</IL>, a placed object will use the physics engine to
              resolve the final position. This means placing an object on an
              uneven surface may cause inconsistent results due to the object
              rolling around or even falling off of the target receptacle. Note
              that because of variances in physics resolution, this placement
              mode is non-deterministic! If <IL>True</IL>, the object will be
              placed in/on the valid receptacle without using physics to resolve
              the final position. This means that the object will be placed so
              that it will not roll around. For deterministic placement make
              sure to set to <IL>True</IL>!
            </p>
          ),
        },
      ],
    },
    DropObject: {
      description: (
        <>
          <p>
            <IL>DropHandObject</IL> attempts to drop an object currently in the
            agent’s hand and let physics resolve where it lands. The action is
            different from <IL>PutObject</IL>, as this does not guarantee the
            held object will be put into a specified receptacle. This is meant
            to be used in tandem with the Move/Rotate Hand functions to maneuver
            a held object to a target area, and the let it drop.
          </p>
          <p>
            Additionally, this drop action will fail if the held object is not
            clear from all collisions. Most importantly, the agent’s collision
            will prevent drop, as dropping an object if it is “inside” the agent
            will lead to unintended behavior.
          </p>
        </>
      ),
      argsTitle: "Drop Object Parameters",
      example: (...parameters) => (
        <>
          <ActionExample actionName="DropHandObject" parameters={parameters} />
          <Video
            id="553876212"
            aspectRatio={1}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        {
          name: "forceAction",
          type: "bool",
          default: "False",
          description: (
            <p>
              Set to <IL>True</IL> to forcibly drop an object even if this would
              cause clipping with the environment, other objects, or the agent.
            </p>
          ),
        },
      ],
    },

    MoveHeldObject: {
      argsTitle: "Move Held Object Parameters",
      description: (
        <>
          <p>
            While the agent is holding an object, it has several available
            actions to manipulate it. One such action is the ability to move the
            held object closer or further away from it. Held object movement is
            useful if we want to drop an object on a surface that is relatively
            far away.
          </p>
          <p>
            There are several directions that the agent can move the held
            object. They include: <IL>MoveHeldObjectAhead</IL>,{" "}
            <IL>MoveHeldObjectBack</IL>, <IL>MoveHeldObjectLeft</IL>,{" "}
            <IL>MoveHeldObjectRight</IL>, <IL>MoveHeldObjectUp</IL>, and{" "}
            <IL>MoveHeldObjectDown</IL>. Each direction supports the same
            additional parameters.
          </p>
          <Warning>
            After calling a move action (e.g., <IL>MoveAhead</IL>) or a rotate
            action (e.g., <IL>RotateRight</IL>) the position of the held object
            will reset to its default position in-front of the agent.
          </Warning>
        </>
      ),
      example: (...parameters) => (
        <>
          <Code>
            {replaceParams(
              String.raw`controller.step(
    action="MoveHeldObjectAhead",
    [params]
)

# Other supported directions
controller.step("MoveHeldObjectBack")
controller.step("MoveHeldObjectLeft")
controller.step("MoveHeldObjectRight")
controller.step("MoveHeldObjectUp")
controller.step("MoveHeldObjectDown")`,
              parameters
            )}
          </Code>
          <Video
            id="553880861"
            aspectRatio={1}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        {
          name: "moveMagnitude",
          required: true,
          type: "float",
          example: "0.1",
          description: (
            <p>
              The distance, in meters, to move the held object in the specified
              direction.
            </p>
          ),
        },
        {
          name: "forceVisible",
          default: "False",
          type: "bool",
          description: (
            <p>
              Setting <IL>forceVisible=True</IL> results in action failing if
              the object is not visible to the agent. This prevents the agent
              from hiding the held object inside or behind another object, or
              moving it too far, such that it's out of the agent's field of
              view.
            </p>
          ),
        },
      ],
    },

    MoveHeldObject2: {
      argsTitle: "Move Held Object Parameters",
      description: (
        <p
          css={css`
            margin-top: 25px;
          `}
        >
          We also provide a separate helper action, <IL>MoveHeldObject</IL>,
          which allows the held object to move in several directions with only a
          single action:
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample actionName="MoveHeldObject" parameters={parameters} />
          <Video
            id="577376697"
            aspectRatio={1}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        {
          name: "ahead",
          required: true,
          type: "float",
          example: 0.1,
          description: (
            <p>
              The distance, in meters, to move the held object forward, from the
              agent's current facing direction.
            </p>
          ),
        },
        {
          name: "right",
          required: true,
          type: "float",
          example: 0.05,
          description: (
            <p>
              The distance, in meters, to move the held object rightwards, from
              the agent's current facing direction.
            </p>
          ),
        },
        {
          name: "up",
          required: true,
          type: "float",
          example: 0.12,
          description: (
            <p>
              The distance, in meters, to move the held object upwards, from the
              agent's current facing direction.
            </p>
          ),
        },
        {
          name: "forceVisible",
          default: "False",
          type: "bool",
          description: (
            <p>
              Setting <IL>forceVisible=True</IL> results in action failing if
              the object is not visible to the agent. This prevents the agent
              from hiding the held object inside or behind another object, or
              moving it too far, such that it's out of the agent's field of
              view.
            </p>
          ),
        },
      ],
    },

    RotateHeldObject: {
      argsTitle: "Rotate Held Object Parameters",
      description: (
        <>
          <p>
            <IL>RotateHeldObject</IL> attempts to rotate the held object
            relative to its current rotation.
          </p>
          <Warning>
            After calling a move action (e.g., <IL>MoveAhead</IL>) or a rotate
            action (e.g., <IL>RotateRight</IL>) the rotation of the held object
            will reset to its default rotation.
          </Warning>
        </>
      ),
      example: (...parameters) => (
        <ActionExample actionName="RotateHeldObject" parameters={parameters} />
      ),
      parameters: [
        {
          name: "pitch",
          default: 0,
          required: true,
          example: 90,
          type: "float",
          description: (
            <>
              <p>
                Increments the pitch of an object from the agent's current
                facing direction. Specified in degrees. The video below shows
                the held object's <IL>pitch</IL> incrementing in{" "}
                <Latex>$15$</Latex> degree increments.
              </p>
              <Video
                id="577392020"
                aspectRatio={1}
                marginTop="10px"
                marginBottom="0px"
              />
            </>
          ),
        },
        {
          name: "yaw",
          default: 0,
          required: true,
          example: 25,
          type: "float",
          description: (
            <>
              <p>
                Increments the yaw of an object from the agent's current facing
                direction. Specified in degrees. The video below shows the held
                object's <IL>yaw</IL> incrementing in <Latex>$15$</Latex> degree
                increments.
              </p>
              <Video
                id="577392033"
                aspectRatio={1}
                marginTop="10px"
                marginBottom="0px"
              />
            </>
          ),
        },
        {
          name: "roll",
          default: 0,
          required: true,
          example: 45,
          type: "float",
          description: (
            <>
              <p>
                Increments the roll of an object from the agent's current facing
                direction. Specified in degrees. The video below shows the held
                object's <IL>roll</IL> incrementing in <Latex>$15$</Latex>{" "}
                degree increments.
              </p>
              <Video
                id="577392050"
                aspectRatio={1}
                marginTop="10px"
                marginBottom="0px"
              />
            </>
          ),
        },
      ],
    },

    RotateHeldObject2: {
      argsTitle: "Rotate Held Object Parameters",
      description: (
        <p
          css={css`
            margin-top: 25px;
          `}
        >
          An overload of <IL>RotateHeldObject</IL> can be used to rotate the
          held object to a fixed rotation.
        </p>
      ),
      example: (...parameters) => (
        <ActionExample actionName="RotateHeldObject" parameters={parameters} />
      ),
      parameters: [
        {
          name: "rotation",
          required: true,
          example: "dict(x=90, y=15, z=25)",
          type: "dict[str, float]",
          description: (
            <p>
              Sets the rotation of the object to the provided rotation, in
              degrees. The rotation is relative to the object's axes. So, the
              set rotation is independent of the object's current rotation.
              Rotations are specified in degrees.
            </p>
          ),
        },
      ],
    },

    ThrowObject: {
      argsTitle: "Throw Object Parameters",
      description: (
        <p>
          <IL>ThrowObject</IL> is an extension of <IL>DropHandObject</IL>. It
          throws the object currently in the agent’s hand in the forward facing
          direction of the agent with a force of <IL>moveMagnitude</IL> newtons.
          Since objects have different mass properties, objects require
          different forces to throw them same distance.
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample actionName="ThrowObject" parameters={parameters} />
          <Video
            id="553879820"
            aspectRatio={1}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        {
          name: "moveMagnitude",
          type: "float",
          required: true,
          example: "150.0",
          description: (
            <p>
              The amount of force used to throw the object in newtons. Note that
              objects of different masses will have different throw distances if
              this magnitude is not changed.
            </p>
          ),
        },
        {
          name: "forceAction",
          type: "bool",
          default: "False",
          description: (
            <p>
              Set to <IL>True</IL> to forcibly throw an object even if this
              would cause clipping with the environment or other objects.
            </p>
          ),
        },
      ],
    },

    Crouch: {
      argsTitle: "Crouch Description",
      rightSide: (
        <p>
          <Latex>
            Crouches the agent from a standing position. The standing position
            is roughly $0.22$ meters taller than the crouching position.
          </Latex>
        </p>
      ),
      description: <></>,
      example: (...parameters) => (
        <>
          <Code>controller.step(action="Crouch")</Code>
          <Video
            id="553129495"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [],
    },

    Stand: {
      argsTitle: "Stand Description",
      rightSide: (
        <p>
          <Latex>
            Stands the agent from a crouching position. The crouching position
            is roughly $0.22$ meters shorter than the standing position.
          </Latex>
        </p>
      ),
      description: <></>,
      example: (...parameters) => (
        <>
          <Code>controller.step(action="Stand")</Code>
          <Video
            id="553129009"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [],
    },

    Teleport: {
      argsTitle: "Teleport Parameters",
      description: (
        <p>
          <IL>Teleport</IL> allows the agent to set its pose to anywhere in the
          scene in a single step. Valid poses do not place the agent outside the
          outer boundaries of the scene or in an area that it collides with an
          object.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>
            {replaceParams(
              String.raw`controller.step(
    action="Teleport",
    [params]
)`,
              parameters
            )}
          </Code>
          <Video
            id="553122867"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        {
          name: "position",
          type: "Optional[dict[str, float]]",
          default: "None",
          description: (
            <p>
              The position of the agent's body in global 3D coordinate space. If
              unspecified, the position of the agent remains the same.
            </p>
          ),
          example: "dict(x=1, y=0.9, z=-1.5)",
        },
        {
          name: "rotation",
          type: "Optional[dict[str, float]]",
          default: "None",
          example: "dict(x=0, y=270, z=0)",
          description: (
            <>
              <p>
                The rotation of the agent's body in global 3D space. Here, the
                rotation changes the{" "}
                <Link to="#sub-agent-rotation">agent's yaw rotation</Link>.
              </p>
              {/* Passing in the <IL>rotation</IL> as a float is equivalent to */}
              {/* passing in <IL>dict(x=0, y=rotation, z=0)</IL>. */}
              <Remark>
                <p>
                  <Latex>
                    The default agent's body cannot change in pitch or roll;
                    hence, why the $$x, z=0$$.
                  </Latex>
                </p>
              </Remark>
              <p>If unspecified, the rotation of the agent remains the same.</p>
            </>
          ),
        },
        {
          name: "horizon",
          type: "Optional[float]",
          default: "None",
          example: "30",
          description: (
            <>
              <p>
                The <IL>horizon</IL> change's the{" "}
                <Link to="#sub-camera-rotation">camera's rotation</Link>. Values
                are clamped between <Latex>[-30:60]</Latex>.
              </p>
              <p>
                Since the agent looks up and down in{" "}
                <Latex>
                  $$30^\circ$$ increments, it most common for the horizon to be
                  in $$\lbrace -30, 0, 30, 60\rbrace$$.
                </Latex>{" "}
              </p>
              <Warning>
                <p>
                  Negative camera <IL>horizon</IL> values correspond to agent
                  looking up, whereas positive <IL>horizon</IL> values
                  correspond to the agent looking down.
                </p>
              </Warning>
              <p>If unspecified, the horizon of the agent remains the same.</p>
            </>
          ),
        },
        {
          name: "standing",
          type: "Optional[bool]",
          default: "None",
          example: "True",
          description: (
            <p>
              Denotes whether the agent is in a standing or crouched position.
              If unspecified, the standing state of the agent remains the same.
            </p>
          ),
        },
      ],
    },

    TeleportFull: {
      argsTitle: "TeleportFull Parameters",
      description: (
        <>
          <p>
            In addition to being able to <IL>Teleport</IL>, we also have an
            equivalent action <IL>TeleportFull</IL> that strictly requires every
            degree of freedom to be specified. The strictness of this action is
            often useful in large projects, where we don't want to any part of
            the agent implied implicitly.
          </p>
          <Warning>
            <p>
              <IL>TeleportFull</IL> does not guarantee backwards compatibility
              in future releases. If a new degree of freedom is added to the
              agent, it will be added as a required parameter. If this is an
              issue, we recommend using <IL>Teleport</IL>.
            </p>
          </Warning>
        </>
      ),
      example: (...parameters) => (
        <Code>
          {replaceParams(
            String.raw`controller.step(
    action="TeleportFull",
    [params]
)`,
            parameters
          )}
        </Code>
      ),
      parameters: [
        {
          name: "position",
          type: "dict[str, float]",
          description: (
            <p>
              The position of the agent's body in global 3D coordinate space.
            </p>
          ),
          required: true,
          example: "dict(x=1, y=0.9, z=-1.5)",
        },
        {
          name: "rotation",
          type: "dict[str, float]",
          example: "dict(x=0, y=270, z=0)",
          description: (
            <>
              <p>
                The rotation of the agent's body in global 3D space. Here, the
                rotation changes the{" "}
                <Link to="#sub-agent-rotation">agent's yaw rotation</Link>.
              </p>
              {/* Passing in the <IL>rotation</IL> as a float is equivalent to */}
              {/* passing in <IL>dict(x=0, y=rotation, z=0)</IL>. */}
              <Remark>
                <p>
                  <Latex>
                    The default agent's body cannot change in pitch or roll;
                    hence, why the $$x, z=0$$.
                  </Latex>
                </p>
              </Remark>
            </>
          ),
          required: true,
        },
        {
          name: "horizon",
          type: "float",
          required: true,
          example: "30",
          description: (
            <>
              <p>
                The <IL>horizon</IL> change's the{" "}
                <Link to="#sub-camera-rotation">camera's rotation</Link>. Values
                are clamped between <Latex>[-30:60]</Latex>.
              </p>
              <p>
                Since the agent looks up and down in{" "}
                <Latex>
                  $$30^\circ$$ increments, it most common for the horizon to be
                  in $$\lbrace -30, 0, 30, 60\rbrace$$.
                </Latex>
              </p>
              <Warning>
                <p>
                  Negative camera <IL>horizon</IL> values correspond to agent
                  looking up, whereas positive <IL>horizon</IL> values
                  correspond to the agent looking down.
                </p>
              </Warning>
            </>
          ),
        },
        {
          name: "standing",
          type: "bool",
          required: true,
          example: "True",
          description: (
            <p>
              Denotes whether the agent is in a standing or crouched position.
            </p>
          ),
        },
      ],
    },
    OpenObjectResponse: {
      argsTitle: "Open Object Response",
      description: (
        <p>
          Each object contains metadata in the event pertaining to the state of
          its openness.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>event.metadata["objects"][i]</Code>
          <Response
            includeExample={true}
            lines={[
              "{",
              '    "openable": True,',
              '    "openness": 0.75,',
              '    "isOpen": True,',
              "",
              "    {...}",
              "}",
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "openable",
          type: "bool",
          description: <p>Can this object be opened?</p>,
        },
        {
          name: "openness",
          type: "float",
          description: (
            <p>
              <Latex>
                The proportion that the object is open, linearly scaled to the
                range $$[0:1]$$. For instance, if an object is a quarter of the
                way open, then its openness would be $0.25$.
              </Latex>
            </p>
          ),
        },
        {
          name: "isOpen",
          type: "bool",
          description: (
            <p>
              <IL>False</IL> <Latex>when the object's openness is $0$</Latex>,{" "}
              <IL>True</IL> otherwise.
            </p>
          ),
        },
      ],
    },

    OpenObject: {
      argsTitle: "Open Object Parameters",
      description: (
        <>
          <p>
            <IL>OpenObject</IL> attempts to open an Openable object to a
            specified amount, parameterized by <IL>openness</IL>. The full list
            of openable object types can be filtered on the{" "}
            <Link to="/ithor/documentation/objects/object-types">
              Object Types
            </Link>{" "}
            page.
          </p>
          <Warning>
            <p>
              An object can fail to open if it hits another object as it is
              opening. In this case the action will fail and the target object
              will reset to the position it was last in.
            </p>
          </Warning>
        </>
      ),
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="OpenObject"
            parameters={parameters}
            includeEvent={true}
          />
          <Video
            id="553000090"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        objectId('"Book|0.25|-0.27|0.95"'),
        {
          name: "openness",
          default: 1,
          type: "float",
          description: (
            <p>
              <Latex>
                The proportion regarding how far the object should open. Valid
                values are in $$[0:1]$$, where the value $$0$$ corresponds to
                completely closed, $$1$$ corresponds to completely open, and
                $$0.5$$ corresponds to halfway open, for instance.
              </Latex>
            </p>
          ),
        },
        forceAction,
      ],
    },

    CloseObject: {
      argsTitle: "Close Object Parameters",
      description: (
        <>
          <p>
            <IL>CloseObject</IL> is syntactic sugar for <IL>OpenObject</IL> with
            an <IL>openness</IL> of 0.
          </p>
          <Warning>
            <p>
              An object can fail to close if it hits another object as it is
              opening. In this case the action will fail and the target object
              will reset to the position it was last in.
            </p>
          </Warning>
        </>
      ),
      example: (...parameters) => (
        <>
          <ActionExample actionName="CloseObject" parameters={parameters} />
          <Video
            id="554119431"
            aspectRatio={1}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [objectId('"Book|0.25|-0.27|0.95"'), forceAction],
    },
    BreakObject: {
      argsTitle: "Break Object Parameters",
      description: (
        <p>
          <IL>BreakObject</IL> attempts to break a breakable object. If
          successful, the object appears visibly different. For instance,
          breakable objects may shatter completely into pieces or have their
          screens cracked. The full list of breakable object types can be
          filtered on the{" "}
          <Link to="/ithor/documentation/objects/object-types">
            Object Types
          </Link>{" "}
          page. Broken objects cannot be unbroken (until the scene has reset).
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="BreakObject"
            parameters={parameters}
            includeEvent={true}
          />
          <Video
            id="554120392"
            aspectRatio={1}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [objectId('"Vase|0.25|0.27|-0.95"'), forceAction],
    },

    BreakObjectResponse: {
      argsTitle: "Break Object Response",
      description: (
        <p>
          Each object contains metadata in the event pertaining to if it is
          broken.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>event.metadata["objects"][i]</Code>
          <Response
            includeExample={true}
            lines={[
              "{",
              '    "breakable": True,',
              '    "isBroken": False,',
              "",
              "    {...}",
              "}",
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "breakable",
          type: "bool",
          description: <p>Can this object break?</p>,
        },
        {
          name: "isBroken",
          type: "bool",
          description: <p>Is this object currently broken?</p>,
        },
      ],
    },

    CookObject: {
      argsTitle: "Cook Object Parameters",
      description: (
        <p>
          <IL>CookObject</IL> attempts to switch an object to its cooked state.
          Objects cannot not be uncooked (unless the scene is reset). The full
          list of cookable object types can be filtered on the{" "}
          <Link to="/ithor/documentation/objects/object-types">
            Object Types
          </Link>{" "}
          page.
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="CookObject"
            parameters={parameters}
            includeEvent={true}
          />
          <Video
            id="554125636"
            aspectRatio={1}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [objectId('"Egg|0.25|-0.27|0.95"'), forceAction],
    },

    CookObjectResponse: {
      argsTitle: "Cook Object Response",
      description: (
        <p>
          Each object contains metadata in the event pertaining to if it is
          cooked.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>event.metadata["objects"][i]</Code>
          <Response
            includeExample={true}
            lines={[
              "{",
              '    "cookable": True,',
              '    "isCooked": False,',
              "",
              "    {...}",
              "}",
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "cookable",
          type: "bool",
          description: <p>Can this object be cooked?</p>,
        },
        {
          name: "isCooked",
          type: "bool",
          description: <p>Is this object currently cooked?</p>,
        },
      ],
    },

    SliceObject: {
      argsTitle: "Slice Object Parameters",
      description: (
        <>
          <p>
            <IL>SliceObject</IL> attempts to slice an object.
          </p>
          <p>
            If an object, such as an Apple, is successfully sliced, there may
            include several new AppleSliced objects in the scene and metadata,
            one for each slice. Other objects, like Egg, may only break after
            being sliced. Sliced objects cannot be unsliced (unless the scene is
            reset).
          </p>
          <p>
            The full list of sliceable objects can be filtered on the{" "}
            <Link to="/ithor/documentation/objects/object-types">
              Object Types
            </Link>{" "}
            page.
          </p>
        </>
      ),
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="SliceObject"
            parameters={parameters}
            includeEvent={true}
          />
          <Video
            id="554118380"
            aspectRatio={1}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [objectId('"Potato|0.25|-0.27|0.95"'), forceAction],
    },

    SliceObjectResponse: {
      argsTitle: "Slice Object Response",
      description: (
        <p>
          Each object contains metadata in the event pertaining to if it is
          sliced.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>event.metadata["objects"][i]</Code>
          <Response
            includeExample={true}
            lines={[
              "{",
              '    "sliceable": True,',
              '    "isSliced": False,',
              "",
              "    {...}",
              "}",
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "sliceable",
          type: "bool",
          description: <p>Can this object be sliced?</p>,
        },
        {
          name: "isSliced",
          type: "bool",
          description: <p>Is this object currently sliced?</p>,
        },
      ],
    },

    ToggleOn: {
      argsTitle: "Toggle Object Parameters",
      description: (
        <p>
          <IL>ToggleObjectOn</IL> and <IL>ToggleObjectOff</IL> toggles an object
          between on and off states, respectively. Examples include Lamps, Light
          Switches, Stove Knobs, and Laptops. The full list of toggleable
          objects can be filtered on the{" "}
          <Link to="/ithor/documentation/objects/object-types">
            Object Types
          </Link>{" "}
          page.
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="ToggleObjectOn"
            parameters={parameters}
            caption="Toggle On"
            includeEvent={true}
          />
          <ActionExample
            actionName="ToggleObjectOff"
            parameters={parameters}
            caption="Toggle Off"
            includeEvent={true}
          />
          <Video
            id="553159240"
            aspectRatio={1 / 2}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [objectId('"LightSwitch|0.25|-0.27|0.95"'), forceAction],
    },

    ToggleObjectResponse: {
      argsTitle: "Toggle Object Response",
      description: (
        <p>
          Each object contains metadata in the event pertaining to if it is
          toggled.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>event.metadata["objects"][i]</Code>
          <Response
            includeExample={true}
            lines={[
              "{",
              '    "toggleable": True,',
              '    "isToggled": True,',
              "",
              "    {...}",
              "}",
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "toggleable",
          type: "bool",
          description: <p>Can this object be toggled?</p>,
        },
        {
          name: "isToggled",
          type: "bool",
          description: <p>Is this object currently toggled on?</p>,
        },
      ],
    },

    DirtyObject: {
      argsTitle: "Dirty Object Parameters",
      description: (
        <p>
          <IL>DirtyObject</IL> attempts to make an object look dirty.{" "}
          <IL>CleanObject</IL> can then be used to make the object look clean
          again. The full list of dirtyable object types can be filtered on the{" "}
          <Link to="/ithor/documentation/objects/object-types">
            Object Types
          </Link>{" "}
          page.
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="DirtyObject"
            parameters={parameters}
            includeEvent={true}
            caption="Dirty Object"
          />
          <ActionExample
            actionName="CleanObject"
            parameters={parameters}
            includeEvent={true}
            caption="Clean Object"
          />
          <Video
            id="554128827"
            marginTop="10px"
            marginBottom="0px"
            aspectRatio={1}
          />
        </>
      ),
      parameters: [objectId('"Mug|0.25|-0.27|0.95"'), forceAction],
    },
    DirtyObjectResponse: {
      argsTitle: "Dirty Object Response",
      description: (
        <p>
          Each object contains metadata in the event pertaining to if it is
          toggled.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>event.metadata["objects"][i]</Code>
          <Response
            includeExample={true}
            lines={[
              "{",
              '    "toggleable": True,',
              '    "isToggled": True,',
              "",
              "    {...}",
              "}",
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "toggleable",
          type: "bool",
          description: <p>Can this object be toggled?</p>,
        },
        {
          name: "isToggled",
          type: "bool",
          description: <p>Is this object currently toggled on?</p>,
        },
      ],
    },

    FillObjectWithLiquid: {
      argsTitle: "Fill Object with Liquid Parameters",
      description: (
        <p>
          <IL>FillObjectWithLiquid</IL> attempts to fill an object with{" "}
          <IL>fillLiquid</IL>. Only compatible objects that are empty can be
          filled. The full list of object types that can be filled with liquid
          can be filtered on the{" "}
          <Link to="/ithor/documentation/objects/object-types">
            Object Types
          </Link>{" "}
          page.
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="FillObjectWithLiquid"
            parameters={parameters}
            includeEvent={true}
          />
          <Video
            id="554131066"
            aspectRatio={1}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [
        objectId('"Mug|0.25|-0.27|0.95"'),
        {
          name: "fillLiquid",
          type: "string",
          example: '"coffee"',
          required: true,
          description: (
            <p>
              The type of liquid that fills the object. Valid liquids are{" "}
              <IL>"coffee"</IL>, <IL>"wine"</IL>, and <IL>"water"</IL>.
            </p>
          ),
        },
        forceAction,
      ],
    },
    FillObjectWithLiquidResponse: {
      argsTitle: "Fill Object with Liquid Response",
      description: (
        <p>
          Each object contains metadata in the event pertaining to if it is
          filled with liquid.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>event.metadata["objects"][i]</Code>
          <Response
            includeExample={true}
            lines={[
              "{",
              '    "fillLiquid": "coffee",',
              '    "canFillWithLiquid": True,',
              '    "isFilledWithLiquid": True,',
              "",
              "    {...}",
              "}",
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "fillLiquid",
          type: "Optional[str]",
          description: (
            <p>
              Which liquid is the object filled with? If the object is not
              filled with liquid, it will report <IL>None</IL>. Valid liquids
              are <IL>"coffee"</IL>, <IL>"wine"</IL>, and <IL>"water"</IL>.
            </p>
          ),
        },
        {
          name: "canFillWithLiquid",
          type: "bool",
          description: <p>Can this object be filled with liquid?</p>,
        },
        {
          name: "isFilledWithLiquid",
          type: "bool",
          description: <p>Is this object currently filled with liquid?</p>,
        },
      ],
    },

    EmptyLiquidFromObject: {
      argsTitle: "Empty Liquid From Object Parameters",
      description: (
        <>
          <p>
            <IL>EmptyLiquidFromObject</IL> attempts to empty the liquid from an
            object if it is currently filled with liquid (i.e.,{" "}
            <IL>isFilledWithLiquid</IL> is True).
          </p>
          <Remark>
            If an object is rotated downward far enough, gravity may cause the
            object to lose its liquid automatically, causing{" "}
            <IL>isFilledWithLiquid</IL> to be <IL>False</IL>. Here, the liquid
            will not appear spilled on the ground like a puddle. Instead, it
            disappears.
          </Remark>
        </>
      ),
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="EmptyLiquidFromObject"
            parameters={parameters}
            includeEvent={true}
          />
          <Video
            id="554142091"
            aspectRatio={1}
            marginTop="10px"
            marginBottom="0px"
          />
        </>
      ),
      parameters: [objectId('"Mug|0.25|-0.27|0.95"'), forceAction],
    },
    EmptyLiquidFromObjectResponse: {
      argsTitle: "Empty Liquid from Object Response",
      description: (
        <p>
          Each object contains metadata in the event pertaining to if it can be
          emptied of liquid.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>event.metadata["objects"][i]</Code>
          <Response
            includeExample={true}
            lines={[
              "{",
              '    "fillLiquid": None,',
              '    "canFillWithLiquid": True,',
              '    "isFilledWithLiquid": False,',
              "",
              "    {...}",
              "}",
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "fillLiquid",
          type: "Optional[str]",
          description: (
            <p>
              Which liquid is the object filled with? If the object is not
              filled with liquid, it will report <IL>None</IL>. Valid liquids
              are <IL>"coffee"</IL>, <IL>"wine"</IL>, and <IL>"water"</IL>.
            </p>
          ),
        },
        {
          name: "canFillWithLiquid",
          type: "bool",
          description: <p>Can this object be emptied with liquid?</p>,
        },
        {
          name: "isFilledWithLiquid",
          type: "bool",
          description: <p>Is this object currently filled with liquid?</p>,
        },
      ],
    },

    UseUpObject: {
      argsTitle: "Use Up Object Parameters",
      description: (
        <p>
          <IL>UseUpObject</IL> attempts to use up parts of an object. The action
          works with objects like ToiletPaper, TissueBox, and PaperTowelRoll,
          which each has an full/empty state. The full list of object types that
          can be used up can be filtered on the{" "}
          <Link to="/ithor/documentation/objects/object-types">
            Object Types
          </Link>{" "}
          page.
        </p>
      ),
      example: (...parameters) => (
        <>
          <ActionExample
            actionName="UseUpObject"
            parameters={parameters}
            includeEvent={true}
          />
          <Video
            id="554146415"
            marginTop="10px"
            marginBottom="0px"
            aspectRatio={1}
          />
        </>
      ),
      parameters: [objectId('"ToiletPaper|0.25|-0.27|0.95"'), forceAction],
    },
    UseUpObjectResponse: {
      argsTitle: "Use Up Object Response",
      description: (
        <p>
          Each object contains metadata in the event pertaining to if it is used
          up.
        </p>
      ),
      example: (...parameters) => (
        <>
          <Code>event.metadata["objects"][i]</Code>
          <Response
            includeExample={true}
            lines={[
              "{",
              '    "canBeUsedUp": True,',
              '    "isUsedUp": False,',
              "",
              "    {...}",
              "}",
            ]}
          />
        </>
      ),
      parameters: [
        {
          name: "canBeUsedUp",
          type: "bool",
          description: <p>Can this object be used up?</p>,
        },
        {
          name: "isUsedUp",
          type: "bool",
          description: <p>Is this object currently used up?</p>,
        },
      ],
    },
  },
};
